import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Badge,
  Alert,
  Progress,
  Input,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Collapse
} from 'reactstrap';
import {
  Activity,
  Heart,
  Play,
  Square,
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  Hand,
  Timer,
  Edit3,
  TrendingUp,
  Award,
  Calendar,
  Bluetooth,
  BluetoothOff
} from 'lucide-react';
import axios from 'axios';
import BLEManager from './BLEManager';
import PPGVisualizer from './PPGVisualizer';

const SCENARIOS = {
  SwapFingers: {
    name: 'Swap Fingers',
    description: 'Alternating PPG collection between left and right fingers with BP measurements',
    color: 'primary',
    icon: Hand,
    minSessions: 3,
    estimatedDuration: 8,
    steps: [
      { id: 1, instruction: 'Place the pulse oximeter on your LEFT middle finger and ensure the BP cuff is loosened', duration: 15, type: 'preparation', finger: 'left', hand: 'Left' },
      { id: 2, instruction: 'Collecting PPG data from LEFT middle finger', duration: 60, type: 'ppg', finger: 'left', hand: 'Left' },
      { id: 3, instruction: 'Take off BLE Device and Measure BP via cuff from left arm, then loosen the cuff', duration: 45, type: 'bp', requiresInput: true },
      { id: 4, instruction: 'Rest period - remain still and relaxed', duration: 120, type: 'break' },
      { id: 5, instruction: 'Switch the pulse oximeter to your RIGHT middle finger', duration: 15, type: 'preparation', finger: 'right', hand: 'Right' },
      { id: 6, instruction: 'Collecting PPG data from RIGHT middle finger', duration: 60, type: 'ppg', finger: 'right', hand: 'Right' },
      { id: 7, instruction: 'Take off BLE Device and Measure BP via cuff from left arm, then loosen the cuff', duration: 45, type: 'bp', requiresInput: true },
      { id: 8, instruction: 'Rest period - remain still and relaxed', duration: 120, type: 'break' }
    ]
  },
  SimultaneousMeasurement: {
    name: 'Simultaneous Measurement',
    description: 'Concurrent PPG and BP measurement from right finger and left arm',
    color: 'success',
    icon: Activity,
    minSessions: 3,
    estimatedDuration: 3,
    steps: [
      { id: 1, instruction: 'Place the pulse oximeter on your RIGHT middle finger and prepare the BP cuff on your left arm', duration: 15, type: 'preparation', finger: 'right', hand: 'Right' },
      {
        id: 2,
        instruction: 'Collecting PPG data from RIGHT middle finger while measuring BP from LEFT arm',
        duration: 60,
        type: 'simultaneous',
        finger: 'right',
        hand: 'Right',
        requiresInput: true
      },
      { id: 3, instruction: 'Rest period - remain still and relaxed', duration: 120, type: 'break' }
    ]
  },
  NoCuff: {
    name: 'No Cuff',
    description: 'PPG collection only, alternating between fingers without BP measurements',
    color: 'info',
    icon: Timer,
    minSessions: 3,
    estimatedDuration: 6,
    steps: [
      { id: 1, instruction: 'Place the pulse oximeter on your LEFT middle finger', duration: 15, type: 'preparation', finger: 'left', hand: 'Left' },
      { id: 2, instruction: 'Collecting PPG data from LEFT middle finger (no BP cuff needed)', duration: 60, type: 'ppg', finger: 'left', hand: 'Left', noCuff: true },
      { id: 3, instruction: 'Rest period - remain still and relaxed', duration: 120, type: 'break' },
      { id: 4, instruction: 'Switch the pulse oximeter to your RIGHT middle finger', duration: 15, type: 'preparation', finger: 'right', hand: 'Right' },
      { id: 5, instruction: 'Collecting PPG data from RIGHT middle finger (no BP cuff needed)', duration: 60, type: 'ppg', finger: 'right', hand: 'Right', noCuff: true },
      { id: 6, instruction: 'Rest period - remain still and relaxed', duration: 120, type: 'break' }
    ]
  }
};

const PPGDataCollection = ({ selectedSubject, baseUrl, onBack }) => {
  // State management
  const [dashboardData, setDashboardData] = useState(null);
  const [activeScenario, setActiveScenario] = useState(null);
  const [previousStats, setPreviousStats] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isCollecting, setIsCollecting] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // BLE and data collection
  const [ppgData, setPpgData] = useState([]);
  const [ppgRawData, setPpgRawData] = useState([]);
  const [bleConnected, setBleConnected] = useState(false);
  const [bleDeviceName, setBleDeviceName] = useState(null);
  const collectedDataRef = useRef([]);
  const collectedTimestampsRef = useRef([]);
  const stepStartTimeRef = useRef(null);
  const bleReconnectingRef = useRef(false);
  const pendingGroundTruthRef = useRef(null);
  const visualizerKeyRef = useRef(0);

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showBPModal, setShowBPModal] = useState(false);
  const [bpValues, setBpValues] = useState({ systolic: '', diastolic: '', heartRate: '' });
  const [bpErrors, setBpErrors] = useState([]);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [showScenarioSelection, setShowScenarioSelection] = useState(false);

  // Setup BLE event listeners
  useEffect(() => {
    BLEManager.on('disconnect', handleBLEDisconnect);

    return () => {
      BLEManager.off('disconnect', handleBLEDisconnect);
      if (BLEManager.isCollecting) {
        BLEManager.stopDataCollection();
      }
      if (BLEManager.device?.gatt?.connected) {
        BLEManager.disconnect();
      }
    };
  }, []);

  const handleBLEDisconnect = () => {
    console.warn('BLE device disconnected');
    setBleConnected(false);
    setBleDeviceName(null);

    if (isCollecting && !bleReconnectingRef.current) {
      setError('BLE device disconnected. Please reconnect to continue.');
    }
  };

  const handleReconnectBLE = async () => {
    try {
      bleReconnectingRef.current = true;
      setLoading(true);
      setError(null);

      const connected = await BLEManager.connect();
      if (!connected) {
        throw new Error('Failed to reconnect to BLE device');
      }

      setBleConnected(true);
      setBleDeviceName(BLEManager.device?.name || 'Unknown Device');
      setSuccess('BLE device reconnected successfully!');

      if (isPaused) {
        setIsPaused(false);
      }

      const currentStep = getCurrentStep();
      if (isCollecting && (currentStep?.type === 'ppg' || currentStep?.type === 'simultaneous')) {
        try {
          await startBLECollection();
        } catch (err) {
          console.error('Failed to start collection after reconnect:', err);
          setError('Connected but failed to start data collection: ' + err.message);
        }
      }

      bleReconnectingRef.current = false;
      setLoading(false);
    } catch (err) {
      setError('Failed to reconnect BLE: ' + err.message);
      bleReconnectingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [selectedSubject]);

  useEffect(() => {
    let interval;
    if (isCollecting && !isPaused) {
      const currentStep = getCurrentStep();
      interval = setInterval(() => {
        setElapsedTime(prev => {
          if (currentStep && prev >= currentStep.duration - 1) {
            handleStepComplete();
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCollecting, isPaused, currentStepIndex]);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${baseUrl}web/ppgDashboard`, {
        patientId: selectedSubject.subjectId
      });
      setDashboardData(response.data);
      setActiveScenario(response.data.activeScenario);
      setPreviousStats(response.data.previousScenarioStatistics || []);
      setShowScenarioSelection(!response.data.activeScenario);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard data';
      setError(errorMessage);
      console.error('Error loading dashboard:', err);
    }
    setLoading(false);
  };

  const handleScenarioSelect = async (scenarioType) => {
    setLoading(true);
    setError(null);

    try {
      const scenarioResponse = await axios.post(`${baseUrl}web/ppgScenarioSceneCreate`, {
        patientId: selectedSubject.subjectId,
        scenarioType: scenarioType,
        createType: 'Scenario',
        scenarioId: null
      });

      const newScenarioId = scenarioResponse.data.id;

      for (let i = 0; i < 3; i++) {
        await axios.post(`${baseUrl}web/ppgScenarioSceneCreate`, {
          patientId: selectedSubject.subjectId,
          scenarioType: scenarioType,
          createType: 'Session',
          scenarioId: newScenarioId
        });
      }

      setSuccess(`${SCENARIOS[scenarioType].name} scenario created with 3 sessions!`);
      await loadDashboard();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create scenario';
      setError(errorMessage);
      console.error('Error creating scenario:', err);
    }
    setLoading(false);
  };

  const handleCreateSession = async () => {
    if (!activeScenario) return;

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${baseUrl}web/ppgScenarioSceneCreate`, {
        patientId: selectedSubject.subjectId,
        scenarioType: activeScenario.scenarioType,
        createType: 'Session',
        scenarioId: activeScenario.scenarioId
      });

      setSuccess('New session created successfully!');
      await loadDashboard();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create session';
      setError(errorMessage);
      console.error('Error creating session:', err);
    }
    setLoading(false);
  };

  const handleStartSession = async (sessionId) => {
    try {
      setLoading(true);

      const connected = await BLEManager.connect();
      if (!connected) {
        throw new Error('Failed to connect to BLE device');
      }

      setBleConnected(true);
      setBleDeviceName(BLEManager.device?.name || 'Unknown Device');

      setCurrentSessionId(sessionId);
      setCurrentStepIndex(0);
      setElapsedTime(0);
      setPpgData([]);
      setPpgRawData([]);
      collectedDataRef.current = [];
      collectedTimestampsRef.current = [];
      setIsCollecting(true);
      setError(null);
      setSuccess(null);

      const firstStep = SCENARIOS[activeScenario.scenarioType].steps[0];
      if (firstStep.type === 'ppg' || firstStep.type === 'simultaneous') {
        await startBLECollection();
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to start BLE connection: ' + err.message);
      setBleConnected(false);
      setLoading(false);
    }
  };

  const startBLECollection = async () => {
    if (!BLEManager.device?.gatt?.connected) {
      throw new Error('BLE device not connected');
    }

    console.log('===== STARTING NEW BLE COLLECTION =====');
    console.log('Collection Key:', visualizerKeyRef.current);

    visualizerKeyRef.current += 1;
    console.log('New Visualizer key:', visualizerKeyRef.current);

    setPpgRawData([]);
    setPpgData([]);
    collectedDataRef.current = [];
    collectedTimestampsRef.current = [];

    console.log('Starting data collection...');
    console.log('BLE Manager state before start:', {
      allRawDataLength: BLEManager.allRawData?.length || 0,
      allTimestampsLength: BLEManager.allTimestamps?.length || 0,
      isCollecting: BLEManager.isCollecting
    });

    const startTime = await BLEManager.startDataCollection((data) => {
      setPpgRawData(prev => {
        const newData = [...prev, ...data];
        return newData;
      });

      const timestamp = Date.now();

      const newDataPoints = data.map((value, idx) => ({
        timestamp: timestamp + idx,
        value: value,
        finger: getCurrentStep()?.finger
      }));

      setPpgData(prev => [...prev.slice(-200), ...newDataPoints]);
    });

    stepStartTimeRef.current = startTime;
    console.log('BLE data collection started successfully at:', startTime);
    console.log('=========================================');
  };

  const stopBLECollection = async () => {
    if (!BLEManager.isCollecting) {
      return { rawData: [], timestamps: [] };
    }

    console.log('Stopping BLE collection');
    const collectionData = await BLEManager.stopDataCollection();

    if (collectionData) {
      console.log('Disconnecting BLE to reset state...');
      try {
        await BLEManager.disconnect();
        console.log('BLE disconnected successfully');

        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('Reconnecting BLE...');
        const reconnected = await BLEManager.connect();
        if (reconnected) {
          console.log('BLE reconnected successfully');
          setBleConnected(true);
          setBleDeviceName(BLEManager.device?.name || 'Unknown Device');
        } else {
          console.warn('Failed to reconnect BLE');
          setBleConnected(false);
        }
      } catch (err) {
        console.error('Error during BLE disconnect/reconnect:', err);
        setBleConnected(false);
      }

      return {
        rawData: collectionData.rawData,
        timestamps: collectionData.timestamps,
        startTime: collectionData.startTime,
        endTime: collectionData.endTime
      };
    }

    return { rawData: [], timestamps: [] };
  };

  const base64EncodePPGData = (ppgData) => {
    return btoa(String.fromCharCode.apply(null, ppgData));
  };

  const saveDataWithoutGroundTruth = async (ppgData) => {
    const encodedSignal = base64EncodePPGData(ppgData.rawData);

    const payload = {
      sessionId: currentSessionId,
      deviceData: {
        collectedAt: ppgData.collectedAt,
        signal: encodedSignal,
        bleDeviceId: ppgData.bleDeviceId,
        hand: ppgData.hand,
        timestamps: ppgData.timestamps,
        groundTruth: null
      }
    };

    await axios.post(`${baseUrl}web/ppgDataSave`, payload);

    console.log('PPG data saved WITHOUT ground truth:', {
      dataPoints: ppgData.rawData.length,
      hand: ppgData.hand,
      bleDeviceId: ppgData.bleDeviceId,
      noCuff: true
    });
  };

  const handleStepComplete = async () => {
    const currentStep = getCurrentStep();
    console.log('Step Complete - Current Step:', currentStep);

    if (currentStep?.type === 'preparation') {
      console.log('Preparation step complete, moving to next');
      moveToNextStep();
      return;
    }

    if (currentStep?.type === 'ppg' || currentStep?.type === 'simultaneous') {
      console.log('PPG/Simultaneous step - stopping collection');

      const currentBleDeviceName = BLEManager.device?.name || bleDeviceName || 'Unknown';
      console.log('BLE Device Name captured:', currentBleDeviceName);

      const bleData = await stopBLECollection();

      collectedDataRef.current = bleData.rawData;
      collectedTimestampsRef.current = bleData.timestamps;

      const ppgDataToSave = {
        rawData: bleData.rawData,
        timestamps: bleData.timestamps,
        hand: currentStep.hand,
        collectedAt: Math.floor(Date.now() / 1000),
        bleDeviceId: currentBleDeviceName
      };

      // KEY FIX: Check if this is a NoCuff scenario
      if (currentStep?.noCuff) {
        console.log('NoCuff step - saving data WITHOUT ground truth');

        try {
          setLoading(true);
          await saveDataWithoutGroundTruth(ppgDataToSave);

          collectedDataRef.current = [];
          collectedTimestampsRef.current = [];
          setPpgData([]);
          setPpgRawData([]);

          setLoading(false);

          await moveToNextStep();
        } catch (err) {
          console.error('Error saving NoCuff data:', err);
          setError('Failed to save data: ' + (err.response?.data?.message || err.message));
          setLoading(false);
        }

        return;
      }

      pendingGroundTruthRef.current = ppgDataToSave;

      console.log('PPG data stored for hand:', currentStep.hand, 'device:', currentBleDeviceName, 'requiresInput:', currentStep?.requiresInput);

      if (currentStep?.requiresInput) {
        console.log('Opening BP modal for PPG step');
        setIsPaused(true);
        setBpValues({ systolic: '', diastolic: '', heartRate: '' });
        setBpErrors([]);
        setShowBPModal(true);
        return;
      } else {
        console.log('No ground truth needed, moving to next step');
        moveToNextStep();
        return;
      }
    } else if (currentStep?.type === 'bp' && currentStep?.requiresInput) {
      console.log('BP step - opening modal, pending data:', !!pendingGroundTruthRef.current);
      setIsPaused(true);
      setBpValues({ systolic: '', diastolic: '', heartRate: '' });
      setBpErrors([]);
      setShowBPModal(true);
      return;
    }

    console.log('Other step type, moving to next');
    moveToNextStep();
  };

  const moveToNextStep = async () => {
    const scenario = SCENARIOS[activeScenario.scenarioType];

    if (currentStepIndex < scenario.steps.length - 1) {
      if (currentStepIndex === scenario.steps.length - 2) {
        finishSession();
      }

      setCurrentStepIndex(currentStepIndex + 1);
      setElapsedTime(0);

      const nextStep = scenario.steps[currentStepIndex + 1];
      console.log('Next step:', nextStep);
      if (nextStep.type === 'ppg' || nextStep.type === 'simultaneous') {
        if (!BLEManager.device?.gatt?.connected) {
          console.log('BLE device not connected. Waiting for manual reconnection...');
          setError('Please reconnect BLE device to continue with PPG collection');
          setIsPaused(true);
          return;
        }

        try {
          await startBLECollection();
        } catch (err) {
          console.error('Failed to start BLE collection:', err);
          setError('Failed to start BLE collection. Please reconnect the device.');
          setIsPaused(true);
        }
      }
    } else {
      await handleSessionComplete();
    }
  };

  const finishSession = async () => {
    try {
      await axios.post(`${baseUrl}web/ppgScenarioSceneDone`, {
        id: currentSessionId,
        createType: 'Session'
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to save session';
      setError(errorMessage);
      console.error('Error saving session:', err);
    }
  };

  const handleSessionComplete = async () => {
    setIsCollecting(false);

    if (BLEManager.isCollecting) {
      await BLEManager.stopDataCollection();
    }

    try {

      setCurrentSessionId(null);
      setCurrentStepIndex(0);
      setSuccess('Session completed successfully!');
      await loadDashboard();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to end session';
      setError(errorMessage);
      console.error('Error ending session:', err);
    }
  };

  const validateBPValues = () => {
    const errors = [];

    if (!bpValues.systolic || isNaN(bpValues.systolic) || parseFloat(bpValues.systolic) <= 0) {
      errors.push('Please enter a valid systolic value');
    }

    if (!bpValues.diastolic || isNaN(bpValues.diastolic) || parseFloat(bpValues.diastolic) <= 0) {
      errors.push('Please enter a valid diastolic value');
    }

    if (!bpValues.heartRate || isNaN(bpValues.heartRate) || parseFloat(bpValues.heartRate) <= 0) {
      errors.push('Please enter a valid heart rate value');
    }

    setBpErrors(errors);
    return errors.length === 0;
  };

  const handleBPSubmit = async () => {
    if (!validateBPValues()) {
      return;
    }

    setLoading(true);
    setBpErrors([]);

    try {
      const groundTruth = {
        systolic: parseFloat(bpValues.systolic),
        diastolic: parseFloat(bpValues.diastolic),
        heartRate: parseFloat(bpValues.heartRate)
      };

      if (pendingGroundTruthRef.current) {
        const ppgDataToSave = pendingGroundTruthRef.current;
        const encodedSignal = base64EncodePPGData(ppgDataToSave.rawData);

        const payload = {
          sessionId: currentSessionId,
          deviceData: {
            collectedAt: ppgDataToSave.collectedAt,
            signal: encodedSignal,
            bleDeviceId: ppgDataToSave.bleDeviceId,
            hand: ppgDataToSave.hand,
            timestamps: ppgDataToSave.timestamps,
            groundTruth: groundTruth
          }
        };

        await axios.post(`${baseUrl}web/ppgDataSave`, payload);

        console.log('PPG data saved WITH ground truth:', {
          dataPoints: ppgDataToSave.rawData.length,
          hand: ppgDataToSave.hand,
          bleDeviceId: ppgDataToSave.bleDeviceId,
          groundTruth: groundTruth
        });

        pendingGroundTruthRef.current = null;
      } else {
        console.warn('No pending PPG data found when submitting BP values');
      }

      collectedDataRef.current = [];
      collectedTimestampsRef.current = [];
      setPpgData([]);
      setPpgRawData([]);

      setShowBPModal(false);
      setBpValues({ systolic: '', diastolic: '', heartRate: '' });
      setBpErrors([]);
      setIsPaused(false);
      setLoading(false);

      await moveToNextStep();
    } catch (err) {
      console.error('Error saving data:', err);
      setBpErrors([err.response?.data?.message || 'Failed to save data. Please try again.']);
      setLoading(false);
    }
  };

  const handleFinishScenario = async () => {
    if (!activeScenario) return;

    const completedCount = activeScenario.sessions.filter(s => s.finishedAt !== null && s.finishedAt > 0).length;
    const minRequired = SCENARIOS[activeScenario.scenarioType].minSessions;

    if (completedCount < minRequired) {
      setError(`Please complete at least ${minRequired} sessions before finishing the scenario. Currently completed: ${completedCount}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${baseUrl}web/ppgScenarioSceneDone`, {
        id: activeScenario.scenarioId,
        createType: 'Scenario'
      });

      setSuccess('Scenario completed successfully!');
      setTimeout(() => {
        loadDashboard();
      }, 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to finish scenario';
      setError(errorMessage);
      console.error('Error finishing scenario:', err);
    }
    setLoading(false);
  };

  const getCurrentStep = () => {
    if (!activeScenario) return null;
    return SCENARIOS[activeScenario.scenarioType].steps[currentStepIndex];
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleSessionExpand = (index) => {
    setExpandedSessions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getStepTypeIcon = (type) => {
    switch (type) {
      case 'preparation': return <Hand size={16} />;
      case 'ppg': return <Activity size={16} />;
      case 'simultaneous': return <Activity size={16} />;
      case 'bp': return <Heart size={16} />;
      case 'break': return <Timer size={16} />;
      case 'instruction': return <Edit3 size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStepTypeColor = (type) => {
    switch (type) {
      case 'preparation': return 'info';
      case 'ppg': return 'success';
      case 'simultaneous': return 'success';
      case 'bp': return 'danger';
      case 'break': return 'secondary';
      case 'instruction': return 'warning';
      default: return 'primary';
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0 && secs > 0) {
      return `${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m`;
    } else {
      return `${secs}s`;
    }
  };

  const getTotalDuration = (steps) => {
    return steps.reduce((total, step) => total + step.duration, 0);
  };

  const getProgressPercentage = () => {
    if (!activeScenario) return 0;
    const steps = SCENARIOS[activeScenario.scenarioType].steps;
    return ((currentStepIndex + 1) / steps.length) * 100;
  };

  if (loading && !dashboardData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading experiment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card className="border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardBody className="text-white">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center mb-2">
                <Activity size={32} className="me-3" />
                <div>
                  <h3 className="mb-1 text-white">PPG Data Collection Experiment</h3>
                  <p className="mb-0 text-white-50">
                    {activeScenario
                      ? `${SCENARIOS[activeScenario.scenarioType].name} - Started ${formatTimestamp(activeScenario.startedAt)}`
                      : 'Select a scenario to begin your experiment'}
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              {activeScenario && (
                <Badge color="light" className="px-3 py-2 me-2">
                  <Calendar size={14} className="me-1" />
                  {activeScenario.sessions.filter(s => s.finishedAt && s.finishedAt > 0).length} / {activeScenario.sessions.length} Sessions
                </Badge>
              )}
              <Badge color={bleConnected ? 'success' : 'secondary'} className="px-3 py-2 me-2">
                {bleConnected ? <Bluetooth size={14} className="me-1" /> : <BluetoothOff size={14} className="me-1" />}
                {bleConnected ? (bleDeviceName || 'BLE Connected') : 'BLE Disconnected'}
              </Badge>
              {!bleConnected && isCollecting && (
                <Button color="warning" size="sm" onClick={handleReconnectBLE} disabled={loading}>
                  <Bluetooth size={14} className="me-1" />
                  Reconnect
                </Button>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {error && (
        <Alert color="danger" className="d-flex align-items-center mb-4" toggle={() => setError(null)}>
          <AlertCircle size={20} className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" className="d-flex align-items-center mb-4" toggle={() => setSuccess(null)}>
          <CheckCircle size={20} className="me-2" />
          {success}
        </Alert>
      )}

      {previousStats.length > 0 && (
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <div className="d-flex align-items-center mb-3">
              <Award size={20} className="text-primary me-2" />
              <h5 className="mb-0">Experiment History</h5>
            </div>
            <Row>
              {previousStats.map((stat, index) => (
                <Col md={4} key={index}>
                  <Card className="border-0" style={{ backgroundColor: '#f8f9fa' }}>
                    <CardBody>
                      <div className="d-flex align-items-center mb-2">
                        {React.createElement(SCENARIOS[stat.scenarioType].icon, {
                          size: 24,
                          className: `text-${SCENARIOS[stat.scenarioType].color} me-2`
                        })}
                        <h6 className="mb-0">{SCENARIOS[stat.scenarioType].name}</h6>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <Badge color={SCENARIOS[stat.scenarioType].color} pill className="px-3">
                          {stat.count} completed
                        </Badge>
                        <small className="text-muted">
                          {formatTimestamp(stat.lastTimeStarted)}
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>
      )}

      {showScenarioSelection && (
        <Row>
          {Object.entries(SCENARIOS).map(([key, scenario]) => (
            <Col md={4} key={key} className="mb-4">
              <Card
                className="border-0 shadow h-100 scenario-card"
                style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                onClick={() => handleScenarioSelect(key)}
              >
                <CardBody className="text-center p-4">
                  <div
                    className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: '90px',
                      height: '90px',
                      background: `linear-gradient(135deg, var(--bs-${scenario.color}) 0%, var(--bs-${scenario.color}) 100%)`
                    }}
                  >
                    <scenario.icon size={45} color="white" />
                  </div>
                  <h5 className="mb-3">{scenario.name}</h5>
                  <p className="text-muted small mb-3">{scenario.description}</p>
                  <div className="d-flex justify-content-center gap-2 mb-3">
                    <Badge color={scenario.color} pill className="px-3">
                      {scenario.minSessions} sessions
                    </Badge>
                    <Badge color="light" pill className="px-3">
                      <Clock size={12} className="me-1" />
                      ~{scenario.estimatedDuration * 3} min
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <Button color={scenario.color} block disabled={loading}>
                      {loading ? 'Creating...' : 'Start Experiment'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {activeScenario && currentSessionId && isCollecting && (
        <Row>
          <Col lg={10} className="mx-auto">
            <Card className="border-0 shadow-lg">
              <CardBody className="p-4">
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Session in Progress</h5>
                    <Badge color="warning" className="px-3 py-2">
                      <Clock size={14} className="me-1" />
                      Step {currentStepIndex + 1} of {SCENARIOS[activeScenario.scenarioType].steps.length}
                    </Badge>
                  </div>
                  <Progress
                    value={getProgressPercentage()}
                    className="mb-2"
                    style={{ height: '8px' }}
                  >
                    <div className="progress-bar" style={{ width: `${getProgressPercentage()}%` }}></div>
                  </Progress>
                  <small className="text-muted">
                    {Math.round(getProgressPercentage())}% Complete
                  </small>
                </div>

                {getCurrentStep() && (
                  <div className="mb-4">
                    <Card
                      style={{
                        backgroundColor: '#f8f9fa',
                        border: `3px solid var(--bs-${getStepTypeColor(getCurrentStep().type)})`,
                        borderRadius: '12px'
                      }}
                    >
                      <CardBody className="p-4">
                        <div className="d-flex align-items-start mb-4">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{
                              width: '60px',
                              height: '60px',
                              backgroundColor: `var(--bs-${getStepTypeColor(getCurrentStep().type)})`,
                              color: 'white'
                            }}
                          >
                            {getStepTypeIcon(getCurrentStep().type)}
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <Badge color={getStepTypeColor(getCurrentStep().type)} className="text-uppercase">
                                {getCurrentStep().type}
                              </Badge>
                              {getCurrentStep().finger && (
                                <Badge
                                  color="dark"
                                  style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    padding: '0.4rem 0.8rem'
                                  }}
                                >
                                  {getCurrentStep().hand === 'Left' ? '👈' : '👉'} {getCurrentStep().hand.toUpperCase()} Hand
                                </Badge>
                              )}
                            </div>
                            <h4 className="mb-0" style={{ lineHeight: '1.5' }}>
                              {getCurrentStep().instruction}
                            </h4>
                          </div>
                        </div>

                        <div className="text-center mb-4 p-4 bg-white rounded shadow-sm">
                          <small className="text-muted text-uppercase d-block mb-2">Time Remaining</small>
                          <h1 className="display-2 font-monospace mb-2" style={{ fontWeight: '300', color: '#2c3e50' }}>
                            {formatTime(getCurrentStep().duration - elapsedTime)}
                          </h1>
                          <div className="text-muted mb-3">
                            of {formatTime(getCurrentStep().duration)}
                          </div>
                          <Progress
                            value={(elapsedTime / getCurrentStep().duration) * 100}
                            color={getStepTypeColor(getCurrentStep().type)}
                            style={{ height: '12px' }}
                            className="shadow-sm"
                          />
                        </div>

                        {(getCurrentStep().type === 'ppg' || getCurrentStep().type === 'simultaneous') && (
                          <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <strong className="text-success">PPG Signal Monitor</strong>
                                <small className="text-muted d-block">Real-time data from BLE device</small>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {!bleConnected && (
                                  <Button color="warning" size="sm" onClick={handleReconnectBLE} disabled={loading}>
                                    <Bluetooth size={14} className="me-1" />
                                    Reconnect
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="bg-dark rounded p-3 shadow-lg" style={{ height: '300px' }}>
                              <PPGVisualizer
                                key={visualizerKeyRef.current}
                                rawData={ppgRawData}
                                showAll={false}
                              />
                            </div>
                          </div>
                        )}

                        {getCurrentStep().type !== 'ppg' && getCurrentStep().type !== 'simultaneous' && getCurrentStep().type !== 'break' && (
                          <Row className="mt-4">
                            <Col>
                              <Button
                                color="warning"
                                onClick={() => setIsPaused(!isPaused)}
                                block
                                size="lg"
                                disabled={loading}
                              >
                                {isPaused ? <Play size={20} /> : <Square size={20} />}
                                {isPaused ? ' Resume' : ' Pause'}
                              </Button>
                            </Col>
                            <Col>
                              <Button
                                color="primary"
                                onClick={handleStepComplete}
                                block
                                size="lg"
                                disabled={loading}
                              >
                                <CheckCircle size={20} className="me-2" />
                                Complete Step
                              </Button>
                            </Col>
                          </Row>
                        )}

                        {(getCurrentStep().type === 'ppg' || getCurrentStep().type === 'simultaneous' || getCurrentStep().type === 'break') && (
                          <Alert color="info" className="mt-4 mb-0">
                            <Clock size={16} className="me-2" />
                            {getCurrentStep().type === 'break'
                              ? 'Please remain still and relaxed. This step will automatically proceed when the timer reaches zero.'
                              : 'Please remain still and avoid movement. Data collection will complete automatically when the timer reaches zero.'}
                          </Alert>
                        )}

                        {!bleConnected && (getCurrentStep()?.type === 'ppg' || getCurrentStep()?.type === 'simultaneous') && (
                          <Alert color="warning" className="mt-3 mb-0">
                            <AlertCircle size={16} className="me-2" />
                            <strong>BLE Device Disconnected!</strong> Please reconnect the device to continue collecting data.
                          </Alert>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {activeScenario && !currentSessionId && (
        <div>
          <Card className="border-0 shadow-sm mb-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Session Management</h5>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleCreateSession}
                    disabled={loading}
                  >
                    <PlusCircle size={16} className="me-1" />
                    Add Session
                  </Button>
                  <Button
                    color="success"
                    size="sm"
                    onClick={handleFinishScenario}
                    disabled={loading || activeScenario.sessions.filter(s => s.finishedAt !== null && s.finishedAt > 0).length < SCENARIOS[activeScenario.scenarioType].minSessions}
                  >
                    <CheckCircle size={16} className="me-1" />
                    Finish Experiment
                  </Button>
                </div>
              </div>

              <Row className="mb-4">
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <CardBody className="text-center">
                      <h3 className="text-success mb-0">
                        {activeScenario.sessions.filter(s => s.finishedAt !== null && s.finishedAt > 0).length}
                      </h3>
                      <small className="text-muted">Completed</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <CardBody className="text-center">
                      <h3 className="text-warning mb-0">
                        {activeScenario.sessions.filter(s => s.finishedAt === null || s.finishedAt === 0).length}
                      </h3>
                      <small className="text-muted">Pending</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <CardBody className="text-center">
                      <h3 className="text-primary mb-0">
                        {activeScenario.sessions.length}
                      </h3>
                      <small className="text-muted">Total Sessions</small>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Progress multi className="mb-4" style={{ height: '8px' }}>
                <Progress
                  bar
                  color="success"
                  value={(activeScenario.sessions.filter(s => s.finishedAt !== null && s.finishedAt > 0).length / activeScenario.sessions.length) * 100}
                />
              </Progress>

              <div className="sessions-list">
                {activeScenario.sessions.map((session, index) => (
                  <Card key={session.sessionId} className="mb-3 border-0 shadow-sm">
                    <CardBody>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center flex-grow-1">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: '40px',
                              height: '40px',
                              backgroundColor: (session.finishedAt && session.finishedAt > 0) ? '#d4edda' : '#fff3cd'
                            }}
                          >
                            <strong>{index + 1}</strong>
                          </div>
                          <div>
                            <h6 className="mb-1">Session {index + 1}</h6>

                            {(session.finishedAt > 0) && (
                              <small className="text-muted d-block">
                                Finished: {formatTimestamp(session.finishedAt)}
                              </small>
                            )}
                          </div>
                          <div className="ms-3">
                            <Badge color={session.finishedAt && session.finishedAt > 0 ? 'success' : 'warning'}>
                              {session.finishedAt && session.finishedAt > 0 ? 'Completed' : 'Pending'}
                            </Badge>
                            <Badge color="light" className="ms-2">
                              <Clock size={12} className="me-1" />
                              ~{SCENARIOS[activeScenario.scenarioType].estimatedDuration} min
                            </Badge>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          {(!session.finishedAt || session.finishedAt === 0) && (
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handleStartSession(session.sessionId)}
                              disabled={loading}
                            >
                              <Play size={16} className="me-1" />
                              Start
                            </Button>
                          )}
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => toggleSessionExpand(index)}
                          >
                            {expandedSessions[index] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </Button>
                        </div>
                      </div>

                      <Collapse isOpen={expandedSessions[index]}>
                        <div className="mt-3 pt-3 border-top">
                          {session.finishedAt > 0 && session.deviceData && (
                            <div className="mb-3">
                              <h6 className="mb-2">Ground Truth Measurements:</h6>
                              <Table size="sm" bordered>
                                <thead>
                                  <tr>
                                    <th>Measurement</th>
                                    <th>Hand</th>
                                    <th>Systolic</th>
                                    <th>Diastolic</th>
                                    <th>Heart Rate</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {session.deviceData.map((gt, gtIndex) => (
                                    <tr key={gtIndex}>
                                      <td>
                                        <Badge color="info">
                                          #{gtIndex + 1}
                                        </Badge>
                                      </td>
                                      <td>
                                        <Badge color="dark">
                                          {gt.hand === 'Left' ? '👈' : '👉'} {gt.hand}
                                        </Badge>
                                      </td>
                                      <td>{gt.groundTruth?.systolic ? `${gt.groundTruth.systolic} mmHg` : 'N/A'}</td>
                                      <td>{gt.groundTruth?.diastolic ? `${gt.groundTruth.diastolic} mmHg` : 'N/A'}</td>
                                      <td>{gt.groundTruth?.heartRate ? `${gt.groundTruth.heartRate} BPM` : 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          )}

                          <h6 className="mb-3">Protocol Steps:</h6>
                          <Table size="sm" className="mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th width="40"></th>
                                <th>Step</th>
                                <th>Instruction</th>
                                <th width="100" className="text-end">Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              {SCENARIOS[activeScenario.scenarioType].steps.map((step, stepIndex) => (
                                <tr key={stepIndex}>
                                  <td width="40">
                                    <div className={`text-${getStepTypeColor(step.type)}`}>
                                      {getStepTypeIcon(step.type)}
                                    </div>
                                  </td>
                                  <td>
                                    <Badge color={getStepTypeColor(step.type)} className="text-uppercase">
                                      {step.type}
                                    </Badge>
                                    {step.hand && (
                                      <Badge color="dark" className="ms-1">
                                        {step.hand === 'Left' ? '👈' : '👉'} {step.hand}
                                      </Badge>
                                    )}
                                  </td>
                                  <td>{step.instruction}</td>
                                  <td width="100" className="text-end">
                                    <Badge color="light" className="font-monospace">
                                      {formatDuration(step.duration)}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-light">
                              <tr>
                                <td colSpan="3" className="text-end"><strong>Total Duration:</strong></td>
                                <td className="text-end">
                                  <Badge color="primary" className="font-monospace">
                                    {formatDuration(getTotalDuration(SCENARIOS[activeScenario.scenarioType].steps))}
                                  </Badge>
                                </td>
                              </tr>
                            </tfoot>
                          </Table>
                        </div>
                      </Collapse>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      <Modal isOpen={showBPModal} backdrop="static" keyboard={false} size="md">
        <ModalHeader>Enter Blood Pressure Measurement</ModalHeader>
        <ModalBody>
          {bpErrors.length > 0 && (
            <Alert color="danger" className="mb-3">
              <AlertCircle size={16} className="me-2" />
              <div>
                {bpErrors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </div>
            </Alert>
          )}

          <div className="mb-3 p-3 bg-light rounded">
            <div className="d-flex align-items-center">
              <Heart size={20} className="text-danger me-2" />
              <div>
                <strong>Ground Truth Measurement</strong>
                <div className="text-muted small">
                  Please enter the cuff measurement values
                </div>
              </div>
            </div>
          </div>
          <FormGroup>
            <Label>Systolic (mmHg) *</Label>
            <Input
              type="number"
              value={bpValues.systolic}
              onChange={(e) => setBpValues({ ...bpValues, systolic: e.target.value })}
              placeholder="120"
              disabled={loading}
              className="form-control-lg"
            />
          </FormGroup>
          <FormGroup>
            <Label>Diastolic (mmHg) *</Label>
            <Input
              type="number"
              value={bpValues.diastolic}
              onChange={(e) => setBpValues({ ...bpValues, diastolic: e.target.value })}
              placeholder="80"
              disabled={loading}
              className="form-control-lg"
            />
          </FormGroup>
          <FormGroup>
            <Label>Heart Rate (BPM) *</Label>
            <Input
              type="number"
              value={bpValues.heartRate}
              onChange={(e) => setBpValues({ ...bpValues, heartRate: e.target.value })}
              placeholder="72"
              disabled={loading}
              className="form-control-lg"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleBPSubmit} disabled={loading} size="lg" block>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircle size={18} className="me-2" />
                Confirm & Continue
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        .scenario-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.15) !important;
        }
        .sessions-list {
          max-height: 800px;
          overflow-y: auto;
        }
      `}</style>
    </div>
  );
};

export default PPGDataCollection;